// L'EFFACEUR — Page Subprocessors (RGPD article 28)
// Liste publique de tous les sous-traitants avec localisation et finalité.
// Obligation légale RGPD pour tout responsable de traitement.

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Globe } from 'lucide-react';

const SUBPROCESSORS = [
  {
    name: 'Render',
    purpose: 'Hébergement backend (FastAPI)',
    location: 'États-Unis (Frankfurt EU pour services)',
    safeguard: 'Standard Contractual Clauses (SCC)',
    website: 'https://render.com',
    dataAccessed: 'Toutes les données utilisateurs (transit + traitement)',
  },
  {
    name: 'Vercel',
    purpose: 'Hébergement frontend (React)',
    location: 'États-Unis',
    safeguard: 'Standard Contractual Clauses (SCC)',
    website: 'https://vercel.com',
    dataAccessed: 'Pas de données utilisateurs (frontend statique)',
  },
  {
    name: 'MongoDB Atlas',
    purpose: 'Base de données',
    location: 'Frankfurt, Allemagne (UE)',
    safeguard: 'UE — pas de transfert hors UE',
    website: 'https://mongodb.com',
    dataAccessed: 'Toutes les données utilisateurs',
  },
  {
    name: 'Stripe',
    purpose: 'Traitement des paiements',
    location: 'Irlande (UE) + États-Unis',
    safeguard: 'Standard Contractual Clauses (SCC) + EU-US Data Privacy Framework',
    website: 'https://stripe.com',
    dataAccessed: 'Email, nom, montant, infos paiement',
  },
  {
    name: 'Brevo (ex-Sendinblue)',
    purpose: 'Envoi emails transactionnels',
    location: 'Paris, France',
    safeguard: 'France/UE — RGPD natif',
    website: 'https://brevo.com',
    dataAccessed: 'Email, contenu emails',
  },
  {
    name: 'Hostinger',
    purpose: 'Domaine + email pro `leffaceur@effaceurpro.fr`',
    location: 'Lituanie (UE)',
    safeguard: 'UE — RGPD natif',
    website: 'https://hostinger.com',
    dataAccessed: 'Emails entrants/sortants pro',
  },
  {
    name: 'Google (Gmail OAuth)',
    purpose: 'Authentification Gmail (admin uniquement, optionnel)',
    location: 'États-Unis',
    safeguard: 'EU-US Data Privacy Framework',
    website: 'https://google.com',
    dataAccessed: "Emails du compte Gmail connecté par l'admin",
  },
  {
    name: 'Cloudflare Turnstile',
    purpose: 'Protection anti-bot CAPTCHA',
    location: 'États-Unis',
    safeguard: 'Standard Contractual Clauses (SCC)',
    website: 'https://www.cloudflare.com',
    dataAccessed: 'IP utilisateur + comportement (anti-bot)',
  },
  {
    name: 'Sentry',
    purpose: 'Monitoring des erreurs (optionnel)',
    location: 'États-Unis',
    safeguard: 'Standard Contractual Clauses (SCC)',
    website: 'https://sentry.io',
    dataAccessed: 'Stack traces erreurs (sans données personnelles intentionnelles)',
  },
];


const Subprocessors = () => {
  return (
    <div className="min-h-screen bg-void text-white">
      <header className="border-b border-gray-800 bg-black/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          <Link to="/" className="text-gray-500 hover:text-cyan-400 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-orbitron text-lg font-black tracking-wider">
            <span className="text-cyan-400">L'</span>EFFACEUR
            <span className="text-gray-500 text-sm ml-2 font-mono">// SUBPROCESSORS</span>
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-cyan-400" />
            <div>
              <h2 className="font-orbitron text-3xl font-bold">Sous-traitants</h2>
              <p className="text-gray-400 text-sm font-mono">// RGPD ARTICLE 28</p>
            </div>
          </div>

          <div className="glass-card border border-cyan-500/20 p-6 mb-8">
            <p className="text-gray-300 leading-relaxed">
              Conformément au RGPD (article 28), nous publions la liste de tous les sous-traitants
              qui traitent des données personnelles de nos utilisateurs pour notre compte.
              Cette liste est mise à jour à chaque ajout ou changement de prestataire.
            </p>
            <p className="text-gray-400 text-sm mt-3">
              <strong>Date de dernière mise à jour :</strong> 27 avril 2026
            </p>
          </div>

          <div className="space-y-4">
            {SUBPROCESSORS.map((sp) => (
              <motion.div
                key={sp.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="glass-card border border-gray-800 p-5 hover:border-cyan-500/30 transition-colors"
              >
                <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                  <div>
                    <h3 className="font-orbitron text-lg font-bold text-cyan-400 mb-1">{sp.name}</h3>
                    <p className="text-gray-300 text-sm">{sp.purpose}</p>
                  </div>
                  <a
                    href={sp.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-500 hover:text-cyan-400 font-mono"
                  >
                    {sp.website.replace('https://', '')} ↗
                  </a>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  <div className="flex items-start gap-2">
                    <Globe className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-gray-500 font-mono uppercase mb-1">Localisation</div>
                      <div className="text-gray-300">{sp.location}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-mono uppercase mb-1">Garanties</div>
                    <div className="text-gray-300">{sp.safeguard}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 font-mono uppercase mb-1">Données traitées</div>
                    <div className="text-gray-300">{sp.dataAccessed}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 glass-card border border-gray-800 p-6">
            <h3 className="font-orbitron text-base font-bold text-white mb-3">Questions ou demandes RGPD</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-3">
              Pour exercer vos droits (accès, rectification, suppression, portabilité, opposition),
              ou pour toute question sur le traitement de vos données :
            </p>
            <div className="space-y-2 text-sm">
              <div>
                <strong className="text-cyan-400">Email DPO :</strong>{' '}
                <a href="mailto:dpo@effaceurpro.fr" className="text-gray-300 hover:text-cyan-400">dpo@effaceurpro.fr</a>
              </div>
              <div>
                <strong className="text-cyan-400">Email général :</strong>{' '}
                <a href="mailto:leffaceur@effaceurpro.fr" className="text-gray-300 hover:text-cyan-400">leffaceur@effaceurpro.fr</a>
              </div>
              <div>
                <strong className="text-cyan-400">Délai de réponse :</strong>{' '}
                <span className="text-gray-300">30 jours maximum (RGPD article 12)</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-800">
              <Link to="/confidentialite" className="text-cyan-400 hover:underline text-sm">
                Charte de Confidentialité complète →
              </Link>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Subprocessors;
